import type { Journey } from "../../types/journey";

export const IVF_JOURNEY: Journey = {
	id: "ivf",
	type: "ivf",
	name: "IVF Journey",
	treatmentType: "IVF Treatment",
	totalSteps: 9,
	steps: [
		{
			id: "ivf-step-1-initial-consultation",
			order: 1,
			title: "Initial Consultation",
			description:
				"This step is where your fertility journey takes shape. Bring your medical history, meet your RE, and leave with a personalised treatment plan.",
			estimatedDateRange: "[Pre-cycle] Est. Week 1 – Week 2",
			status: "completed",
			subSteps: [
				{
					id: "ivf-step-1-initial-consultation-ss-01",
					title: "Prepare documentation & records",
				},
				{
					id: "ivf-step-1-initial-consultation-ss-02",
					title: "Book & attend consultation",
				},
				{
					id: "ivf-step-1-initial-consultation-ss-03",
					title: "Review outcomes & next steps",
				},
			],
			highlight: {
				dayCount: "7–14 days",
				monitoringSchedule:
					"Prepare medical history before appointment; Review treatment plan within 24 hrs of consultation",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"It's common to feel overwhelmed after a first consultation. Writing down questions beforehand helps many people feel more in control.",
				milestone: "Treatment Plan Received, est. Week 2",
				currentDayLabel: "Day [N] of ~14",
				estimatedLengthDays: 14,
			},
			tasks: [
				{
					id: "ivf-step-1-initial-consultation-ss1-task1",
					title: "Fill: Patient Intake Form",
					description: "Personal details for your clinic file",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-1-initial-consultation-ss-01",
					subStepTitle: "Prepare documentation & records",
					progressTotal: 8,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-1-initial-consultation-ss1-task2",
					title: "Fill: Health Questionnaire",
					description: "Medical history information for your next visit",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-1-initial-consultation-ss-01",
					subStepTitle: "Prepare documentation & records",
					progressTotal: 12,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-1-initial-consultation-ss1-task3",
					title: "Upload: Previous Medical Records",
					description: "Prior treatment history and findings",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-1-initial-consultation-ss-01",
					subStepTitle: "Prepare documentation & records",
				},
				{
					id: "ivf-step-1-initial-consultation-ss1-task4",
					title: "Upload: Referral Letter",
					description: "Letter from your GP or referring physician",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-1-initial-consultation-ss-01",
					subStepTitle: "Prepare documentation & records",
				},
				{
					id: "ivf-step-1-initial-consultation-ss1-task5",
					title: "Upload: ID Documents",
					description: "Passport or government-issued photo ID",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-1-initial-consultation-ss-01",
					subStepTitle: "Prepare documentation & records",
				},
				{
					id: "ivf-step-1-initial-consultation-ss2-task1",
					title: "Book: Initial Consultation with RE",
					description: "First appointment with your fertility specialist",
					category: "clinical",
					deadline: "As Soon as Possible",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-1-initial-consultation-ss-02",
					subStepTitle: "Book & attend consultation",
				},
				{
					id: "ivf-step-1-initial-consultation-ss2-task2",
					title: "Fill: Pre-Treatment Assessment",
					description: "Baseline health and lifestyle details for your team",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-1-initial-consultation-ss-02",
					subStepTitle: "Book & attend consultation",
					progressTotal: 6,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-1-initial-consultation-ss3-task1",
					title: "Review & Sign: Treatment Plan Overview",
					description: "Your personalised stimulation plan for review",
					category: "clinical",
					deadline: "Within 24 Hours of Consultation",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-1-initial-consultation-ss-03",
					subStepTitle: "Review outcomes & next steps",
				},
				{
					id: "ivf-step-1-initial-consultation-ss3-task2",
					title: "Fill: Billing Details",
					description: "Insurance and payment information for your file",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-1-initial-consultation-ss-03",
					subStepTitle: "Review outcomes & next steps",
					progressTotal: 5,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-1-initial-consultation-ss3-task3",
					title: "Upload: Insurance Authorisation",
					description: "Pre-approval letter from your insurance provider",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-1-initial-consultation-ss-03",
					subStepTitle: "Review outcomes & next steps",
				},
			],
			faqs: [
				{
					id: "ivf-step-1-initial-consultation-faq-1",
					question: "What should I bring to my first appointment?",
					answer:
						"Bring any previous fertility test results, a full list of current medications, insurance information, and a government-issued photo ID. If you've had prior fertility treatment, include those records. A written note of your menstrual cycle history — length, regularity, and any irregularities — gives your RE a complete picture from the start and helps shape the right protocol for your situation.",
				},
				{
					id: "ivf-step-1-initial-consultation-faq-2",
					question: "How long does this first step usually take?",
					answer:
						"The initial consultation itself typically runs 60–90 minutes. After that, it generally takes 5–10 days for all test results to come back and for the clinic to prepare a personalised plan. From first appointment to having a clear picture of next steps is usually 1–2 weeks. Some clinics offer expedited scheduling if time is a factor — it's worth asking at booking.",
				},
				{
					id: "ivf-step-1-initial-consultation-faq-3",
					question: "Does my partner need to attend this step?",
					answer:
						"If you have a partner, attending together is strongly recommended — both partners may need testing, and decisions are often made as a couple. If that's not possible, most clinics can arrange a follow-up call. For those going through treatment solo, this appointment is fully designed to support that path, and your RE will tailor the conversation accordingly.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Initial Consultation.",
		},
		{
			id: "ivf-step-2-fertility-testing",
			order: 2,
			title: "Fertility Testing",
			description:
				"This step builds a complete fertility baseline through blood tests, scans, and uterine assessment — giving the clinical team the data needed to design the right protocol.",
			estimatedDateRange:
				"Est. Apr 1 – Apr 14, 2026 [Pre-cycle, CD2–CD3 for baseline scan]",
			estimatedStartDate: "2026-04-01",
			status: "completed",
			subSteps: [
				{
					id: "ivf-step-2-fertility-testing-ss-01",
					title: "Ovarian reserve testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss-02",
					title: "Hormone panel & baseline scan",
				},
				{
					id: "ivf-step-2-fertility-testing-ss-03",
					title: "Uterine assessment",
				},
				{
					id: "ivf-step-2-fertility-testing-ss-04",
					title: "Partner / sperm testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss-05",
					title: "Genetic carrier screening (optional)",
				},
			],
			highlight: {
				dayCount: "7–14 days",
				monitoringSchedule:
					"Attend baseline scan on CD2 or CD3; Notify clinic immediately when period starts (Day 1)",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Waiting for results can bring a mix of hope and anxiety. Most results are back within a few days, and every number — whatever it shows — is information that helps build a better plan.",
				milestone: "All Results In, est. Apr 14",
				currentDayLabel: "Day [N] of ~14",
				estimatedLengthDays: 14,
			},
			tasks: [
				{
					id: "ivf-step-2-fertility-testing-ss1-task1",
					title: "Book: AMH Blood Test",
					description: "Measure your ovarian reserve before treatment",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss1-task2",
					title: "Test: AMH Blood Test",
					description: "Ovarian reserve blood work for your protocol",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss1-task3",
					title: "Upload: AMH Test Results",
					description: "Ovarian reserve blood work results",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss2-task1",
					title: "Notify: Period Started — Day 1",
					description: "Log cycle day 1 to schedule your baseline scan",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-2-fertility-testing-ss-02",
					subStepTitle: "Hormone panel & baseline scan",
				},
				{
					id: "ivf-step-2-fertility-testing-ss2-task2",
					title: "Book: Baseline Scan",
					description: "Cycle day 2–3 scan before stimulation begins",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-2-fertility-testing-ss-02",
					subStepTitle: "Hormone panel & baseline scan",
				},
				{
					id: "ivf-step-2-fertility-testing-ss2-task3",
					title: "Test: Hormone Panel — E2, FSH, LH",
					description: "Baseline hormone levels before stimulation starts",
					category: "clinical",
					deadline: "Cycle Day 2 or 3",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-2-fertility-testing-ss-02",
					subStepTitle: "Hormone panel & baseline scan",
				},
				{
					id: "ivf-step-2-fertility-testing-ss2-task4",
					title: "Test: Antral Follicle Count Scan",
					description: "Ultrasound count of resting follicles in both ovaries",
					category: "clinical",
					deadline: "Cycle Day 2 or 3",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-2-fertility-testing-ss-02",
					subStepTitle: "Hormone panel & baseline scan",
				},
				{
					id: "ivf-step-2-fertility-testing-ss3-task1",
					title: "Book: Uterine Assessment",
					description: "Assess uterine cavity before your IVF cycle",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-2-fertility-testing-ss-03",
					subStepTitle: "Uterine assessment",
				},
				{
					id: "ivf-step-2-fertility-testing-ss3-task2",
					title: "Test: Uterine Assessment",
					description: "Saline sonogram or hysteroscopy of uterine cavity",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-2-fertility-testing-ss-03",
					subStepTitle: "Uterine assessment",
				},
				{
					id: "ivf-step-2-fertility-testing-ss4-task1",
					title: "Book: Semen Analysis",
					description: "Partner sperm test — coordinate with your clinic",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-2-fertility-testing-ss-04",
					subStepTitle: "Partner / sperm testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss4-task2",
					title: "Test: Semen Analysis",
					description: "Partner sperm count, motility and morphology test",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-2-fertility-testing-ss-04",
					subStepTitle: "Partner / sperm testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss4-task3",
					title: "Upload: Semen Analysis Report",
					description: "Partner lab results for your shared file",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-2-fertility-testing-ss-04",
					subStepTitle: "Partner / sperm testing",
				},
				{
					id: "ivf-step-2-fertility-testing-ss5-task1",
					title: "Book: Carrier Screening",
					description: "Optional genetic screen recommended before IVF",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-2-fertility-testing-ss-05",
					subStepTitle: "Genetic carrier screening (optional)",
				},
				{
					id: "ivf-step-2-fertility-testing-ss5-task2",
					title: "Test: Carrier Screening",
					description: "Screen for recessive genetic conditions",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-2-fertility-testing-ss-05",
					subStepTitle: "Genetic carrier screening (optional)",
				},
				{
					id: "ivf-step-2-fertility-testing-ss5-task3",
					title: "Notify: Test Results Received",
					description: "Let your team know carrier screening results are in",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-2-fertility-testing-ss-05",
					subStepTitle: "Genetic carrier screening (optional)",
				},
			],
			faqs: [
				{
					id: "ivf-step-2-fertility-testing-faq-1",
					question: "What does an AMH test actually measure?",
					answer:
						"AMH — Anti-Müllerian Hormone — is produced by small follicles in the ovaries and provides an estimate of remaining egg supply, known as ovarian reserve. It helps the clinical team calibrate the right stimulation dose. A lower AMH doesn't predict IVF failure — it means the protocol will be adjusted to suit the individual response. Many people with low AMH have successful outcomes with a tailored approach.",
				},
				{
					id: "ivf-step-2-fertility-testing-faq-2",
					question: "Is fasting required before the blood tests?",
					answer:
						"Most fertility blood tests — including AMH and day-2 hormone panels — don't require fasting. It's always worth confirming with the clinic the day before, as some combined panels may have specific requirements. For the baseline scan, an empty bladder is generally preferred for transvaginal ultrasound, though clinics will advise on this when booking.",
				},
				{
					id: "ivf-step-2-fertility-testing-faq-3",
					question:
						"What happens if a result comes back outside the normal range?",
					answer:
						"If any result falls outside the expected range — whether that's a low AMH, elevated FSH, or a finding on the uterine scan — the RE will explain what it means in the context of your specific situation. Many findings that seem concerning at first are entirely workable with the right protocol. This step is about gathering information to build the best possible plan, not about passing or failing a test.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Fertility Testing.",
		},
		{
			id: "ivf-step-3-treatment-preparation",
			order: 3,
			title: "Treatment Preparation",
			description:
				"This step covers the groundwork before the cycle begins — consents, financial setup, and hands-on medication training so everything is in place before Day 1.",
			estimatedDateRange: "Est. Apr 14 – Apr 20, 2026 [Pre-cycle, before CD1]",
			estimatedStartDate: "2026-04-14",
			status: "completed",
			subSteps: [
				{
					id: "ivf-step-3-treatment-preparation-ss-01",
					title: "Financial & insurance setup",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss-02",
					title: "Consent documentation",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss-03",
					title: "Medication training & protocol review",
				},
			],
			highlight: {
				dayCount: "5–7 days",
				monitoringSchedule:
					"Sign all consent forms before cycle day 1; Attend self-injection training before stimulation starts",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Learning to self-inject is a common source of anxiety. Most people feel significantly more confident after one training session — the needles are much finer than expected.",
				milestone: "Protocol Signed & Medications Ready, est. Apr 20",
				currentDayLabel: "Day [N] of ~7",
				estimatedLengthDays: 7,
			},
			tasks: [
				{
					id: "ivf-step-3-treatment-preparation-ss1-task1",
					title: "Fill: Insurance Verification Form",
					description: "Confirm your fertility coverage with your provider",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-3-treatment-preparation-ss-01",
					subStepTitle: "Financial & insurance setup",
					progressTotal: 4,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss1-task2",
					title: "Book: Insurance Agent Call",
					description: "Discuss your coverage for this treatment cycle",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-3-treatment-preparation-ss-01",
					subStepTitle: "Financial & insurance setup",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss1-task3",
					title: "Review & Sign: Financial Agreement",
					description: "Treatment cost breakdown and payment terms",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-3-treatment-preparation-ss-01",
					subStepTitle: "Financial & insurance setup",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss1-task4",
					title: "Upload: Insurance Authorisation",
					description: "Prior authorisation letter from your insurer",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-3-treatment-preparation-ss-01",
					subStepTitle: "Financial & insurance setup",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss2-task1",
					title: "Review & Sign: General Consent Form",
					description: "Standard clinic terms and permissions",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-3-treatment-preparation-ss-02",
					subStepTitle: "Consent documentation",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss2-task2",
					title: "Review & Sign: Informed Consent for IVF",
					description: "IVF procedure risks, benefits and alternatives",
					category: "clinical",
					deadline: "Before Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-3-treatment-preparation-ss-02",
					subStepTitle: "Consent documentation",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss2-task3",
					title: "Review & Sign: Medication Consent",
					description: "Acknowledge your medication risks and dosage plan",
					category: "medication",
					deadline: "Before Stimulation Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-3-treatment-preparation-ss-02",
					subStepTitle: "Consent documentation",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss2-task4",
					title: "Review & Sign: Cryopreservation Consent",
					description: "Embryo storage terms and decision rights",
					category: "clinical",
					deadline: "Before Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-3-treatment-preparation-ss-02",
					subStepTitle: "Consent documentation",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss3-task1",
					title: "Review & Sign: Treatment Protocol",
					description: "Your personalised stimulation plan",
					category: "clinical",
					deadline: "Before Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-3-treatment-preparation-ss-03",
					subStepTitle: "Medication training & protocol review",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss3-task2",
					title: "Book: Self-Injection Training",
					description: "Learn to administer your stimulation medications",
					category: "medication",
					deadline: "Before Stimulation Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-3-treatment-preparation-ss-03",
					subStepTitle: "Medication training & protocol review",
				},
				{
					id: "ivf-step-3-treatment-preparation-ss3-task3",
					title: "Book: Coordinator Check-in",
					description: "Catch up on your plan before the cycle begins",
					category: "clinical",
					deadline: "Before Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-3-treatment-preparation-ss-03",
					subStepTitle: "Medication training & protocol review",
				},
			],
			faqs: [
				{
					id: "ivf-step-3-treatment-preparation-faq-1",
					question: "What does the informed consent process involve?",
					answer:
						"Informed consent covers the risks and benefits of each procedure — stimulation, egg retrieval, and embryo transfer — as well as decisions about embryo storage, future use, donation, or disposal. It's a legal document, but it's also an opportunity to ask questions. Reading it carefully and raising any concerns before signing is encouraged — the goal is genuine understanding, not just a signature.",
				},
				{
					id: "ivf-step-3-treatment-preparation-faq-2",
					question: "What if insurance doesn't cover the treatment?",
					answer:
						"Many IVF cycles are partially or fully self-funded. The clinic's financial coordinator can walk through the full cost breakdown and available payment or financing options. Some employers offer fertility benefits — checking HR policy is worth doing before committing. The financial agreement signed at this step outlines all costs clearly, so there are no surprises once treatment begins.",
				},
				{
					id: "ivf-step-3-treatment-preparation-faq-3",
					question: "How does self-injection training work?",
					answer:
						"The clinic nurse will walk through every step at a dedicated training appointment — how to prepare the medication, where and how to inject, and what to watch for. Key practical points: letting medication reach room temperature for 15 minutes before injecting, rotating sites each evening to reduce bruising, and injecting at the same time each day. The clinic will provide a sharps disposal container and an after-hours contact for any concerns.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Treatment Preparation.",
		},
		{
			id: "ivf-step-4-ovarian-stimulation",
			order: 4,
			title: "Ovarian Stimulation",
			description:
				"This step uses daily hormone injections to stimulate the ovaries to develop multiple follicles. The clinical team monitors response every 2–3 days and adjusts dosage based on results.",
			estimatedDateRange: "Est. Apr 21 – May 2, 2026 [CD1–CD14]",
			estimatedStartDate: "2026-04-21",
			status: "completed",
			subSteps: [
				{
					id: "ivf-step-4-ovarian-stimulation-ss-01",
					title: "Cycle start & baseline",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss-02",
					title: "Daily injections",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss-03",
					title: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss-04",
					title: "Trigger shot",
				},
			],
			highlight: {
				dayCount: "10–14 days",
				monitoringSchedule:
					"Daily injections every evening at the same time; Monitoring scan every 2–3 days; Notify clinic immediately when period starts (Day 1)",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Mood shifts, bloating, and fatigue are common during stimulation — signs the body is responding. Many people find it helpful to keep evenings free around injection time.",
				milestone: "Trigger Shot, est. May 2",
				currentDayLabel: "Day [N] of ~12",
				estimatedLengthDays: 12,
			},
			tasks: [
				{
					id: "ivf-step-4-ovarian-stimulation-ss1-task1",
					title: "Notify: Period Started — Day 1",
					description: "Log cycle day 1 to start your protocol",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-01",
					subStepTitle: "Cycle start & baseline",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss1-task2",
					title: "Book: Baseline Scan — Cycle Day 2–3",
					description: "Confirm ovaries are clear and ready to stimulate",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-01",
					subStepTitle: "Cycle start & baseline",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss1-task3",
					title: "Test: Baseline Scan",
					description: "Confirm ovaries clear before injections begin",
					category: "clinical",
					deadline: "Cycle Day 2 or 3",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-01",
					subStepTitle: "Cycle start & baseline",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss2-task1",
					title: "Notify: Medication Started",
					description: "Confirm your first injection has been administered",
					category: "medication",
					deadline: "Stimulation Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-02",
					subStepTitle: "Daily injections",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss2-task2",
					title: "Monitor: Medication Side Effects",
					description: "Log daily how you're responding to injections",
					category: "medication",
					deadline: "Daily During Stimulation",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-02",
					subStepTitle: "Daily injections",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss2-task3",
					title: "Notify: New Symptom or Side Effect",
					description: "Report anything unexpected to your clinic",
					category: "clinical",
					deadline: "As Soon as It Occurs",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-02",
					subStepTitle: "Daily injections",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task1",
					title: "Book: Monitoring Scan — Cycle Day 6",
					description: "First follicle growth check during stimulation",
					category: "clinical",
					deadline: "By Cycle Day 5",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task2",
					title: "Book: Monitoring Scan — Cycle Day 8",
					description: "Second follicle growth check during stimulation",
					category: "clinical",
					deadline: "By Cycle Day 7",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task3",
					title: "Book: Monitoring Scan — Cycle Day 10",
					description: "Final pre-trigger follicle assessment",
					category: "clinical",
					deadline: "By Cycle Day 9",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task4",
					title: "Test: Monitoring Visit — Scan & Blood Work",
					description: "Follicle size, count and hormone levels checked",
					category: "clinical",
					deadline: "Cycle Days 6, 8 and 10",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task5",
					title: "Monitor: Follicle Growth",
					description: "Track your follicle size and count between scans",
					category: "clinical",
					deadline: "Daily During Stimulation",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task6",
					title: "Monitor: Hormone Levels — E2, P4 & LH",
					description: "Track E2, P4 and LH trends during stimulation",
					category: "clinical",
					deadline: "Daily During Stimulation",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task7",
					title: "Monitor: Cycle Symptoms",
					description: "Track bloating, cramping and spotting daily",
					category: "cycle",
					deadline: "Throughout Stimulation",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss3-task8",
					title: "Book: Medication Review Call",
					description: "Dosage adjustment based on your scan results",
					category: "medication",
					deadline: "After Each Monitoring Scan",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-03",
					subStepTitle: "Monitoring visits",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss4-task1",
					title: "Book: Egg Retrieval",
					description: "Schedule your retrieval ~36 hrs after trigger shot",
					category: "clinical",
					deadline: "Around Cycle Day 11–12",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-04",
					subStepTitle: "Trigger shot",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-ss4-task2",
					title: "Notify: Trigger Shot Done",
					description: "Confirm your hCG injection was given on time",
					category: "medication",
					deadline: "Same Night as Trigger Shot",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-4-ovarian-stimulation-ss-04",
					subStepTitle: "Trigger shot",
				},
			],
			faqs: [
				{
					id: "ivf-step-4-ovarian-stimulation-faq-1",
					question: "Will the daily injections be painful?",
					answer:
						"Most patients describe a brief pinch and mild sting — the needles are very fine and go just under the skin. Letting the medication warm to room temperature for 15 minutes before injecting, and rotating injection sites each night, reduces discomfort noticeably. If spreading redness, hard lumps, or fever develop at the injection site, the clinic should be contacted — these are uncommon but worth reporting.",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-faq-2",
					question: "How many eggs will the stimulation produce?",
					answer:
						"The number of follicles that develop varies based on ovarian reserve, age, and individual response to stimulation. Monitoring visits track follicle count and size so the team can optimise the response. A typical retrieval aims for 8–15 mature eggs, though any number can lead to a successful outcome — quality and embryo development matter as much as the initial count.",
				},
				{
					id: "ivf-step-4-ovarian-stimulation-faq-3",
					question: "What is OHSS and how is it managed?",
					answer:
						"Ovarian Hyperstimulation Syndrome (OHSS) is a condition where the ovaries over-respond to stimulation, causing swelling and discomfort. Mild OHSS is relatively common and typically resolves on its own. Severe OHSS is rare. Monitoring visits are specifically designed to detect early signs of over-response so the team can adjust the protocol. Severe or worsening abdominal pain, difficulty breathing, or inability to keep fluids down should be reported to the clinic immediately.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Ovarian Stimulation.",
		},
		{
			id: "ivf-step-5-egg-retrieval",
			order: 5,
			title: "Egg Retrieval",
			description:
				"This is the procedure day. Under light sedation, mature eggs are collected from the follicles using a fine ultrasound-guided needle. Most patients are home and resting within a few hours.",
			estimatedDateRange:
				"Est. May 3, 2026 [CD14–CD16, ~36 hrs after trigger shot]",
			estimatedStartDate: "2026-05-03",
			status: "completed",
			subSteps: [
				{
					id: "ivf-step-5-egg-retrieval-ss-01",
					title: "Pre-retrieval preparation",
				},
				{
					id: "ivf-step-5-egg-retrieval-ss-02",
					title: "Retrieval procedure",
				},
				{
					id: "ivf-step-5-egg-retrieval-ss-03",
					title: "Post-retrieval recovery",
				},
			],
			highlight: {
				dayCount: "1 days",
				monitoringSchedule:
					"Complete pre-procedure checklist the day before; Arrange a companion to drive home after sedation",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"It's common to feel a mix of relief and emotional intensity after retrieval. Physical recovery is usually quick — rest is the only requirement for the rest of the day.",
				milestone: "Retrieval Complete, est. May 3",
				currentDayLabel: "Day 1 of 1",
				estimatedLengthDays: 1,
			},
			tasks: [
				{
					id: "ivf-step-5-egg-retrieval-ss1-task1",
					title: "Review & Sign: Egg Retrieval Consent",
					description: "Procedure risks, sedation and cancellation terms",
					category: "clinical",
					deadline: "Before Retrieval Day",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-5-egg-retrieval-ss-01",
					subStepTitle: "Pre-retrieval preparation",
				},
				{
					id: "ivf-step-5-egg-retrieval-ss1-task2",
					title: "Fill: Pre-Procedure Checklist",
					description: "Fasting, medication and arrival instructions confirmed",
					category: "clinical",
					deadline: "Day Before Retrieval",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-5-egg-retrieval-ss-01",
					subStepTitle: "Pre-retrieval preparation",
					progressTotal: 5,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-5-egg-retrieval-ss2-task1",
					title: "Test: Egg Retrieval Procedure",
					description: "Ultrasound-guided follicle aspiration under sedation",
					category: "clinical",
					deadline: "Retrieval Day — at your booked time",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-5-egg-retrieval-ss-02",
					subStepTitle: "Retrieval procedure",
				},
				{
					id: "ivf-step-5-egg-retrieval-ss3-task1",
					title: "Monitor: Post-Retrieval Symptoms",
					description: "Log bloating, cramping and spotting after procedure",
					category: "clinical",
					deadline: "24–48 Hours Post Retrieval",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-5-egg-retrieval-ss-03",
					subStepTitle: "Post-retrieval recovery",
				},
				{
					id: "ivf-step-5-egg-retrieval-ss3-task2",
					title: "Notify: New Symptom or Side Effect",
					description: "Report OHSS signs or unexpected pain to your clinic",
					category: "clinical",
					deadline: "As Soon as It Occurs",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-5-egg-retrieval-ss-03",
					subStepTitle: "Post-retrieval recovery",
				},
			],
			faqs: [
				{
					id: "ivf-step-5-egg-retrieval-faq-1",
					question: "What does retrieval day actually involve?",
					answer:
						"The procedure takes approximately 20–30 minutes under light sedation. After arriving fasted, patients rest in recovery for 1–2 hours before going home. Mild cramping and bloating in the following 24–48 hours are common. A companion is required to drive home. The embryology lab calls the next morning with the fertilisation report — the first update on how many eggs fertilised normally.",
				},
				{
					id: "ivf-step-5-egg-retrieval-faq-2",
					question: "How many eggs is it realistic to expect?",
					answer:
						"Not every follicle contains a mature egg, and not every egg retrieved will be suitable for fertilisation. The clinic will share the egg count immediately after the procedure. It's helpful to take each update — egg count, fertilisation report, day-5 blastocyst count — as a separate piece of information rather than extrapolating from one number to the end result.",
				},
				{
					id: "ivf-step-5-egg-retrieval-faq-3",
					question: "What are the risks associated with retrieval?",
					answer:
						"Egg retrieval is a well-established, low-risk procedure. The main considerations include mild bleeding, a small risk of infection, and, rarely, injury to surrounding structures. Thorough pre-screening minimises these risks. After getting home, the following warrant an immediate call to the clinic: severe or worsening pain, heavy bleeding, fever, or difficulty urinating. Most patients recover comfortably within 24–48 hours.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Egg Retrieval.",
		},
		{
			id: "ivf-step-6-embryo-development",
			order: 6,
			title: "Embryo Development",
			description:
				"Over 5–6 days, fertilised eggs are cultured in the embryology lab and monitored as they develop into blastocysts. Key updates come on Day 1, Day 3, and Day 5–6.",
			estimatedDateRange: "Est. May 4 – May 9, 2026 [Day 1–6 post retrieval]",
			estimatedStartDate: "2026-05-04",
			status: "current",
			subSteps: [
				{
					id: "ivf-step-6-embryo-development-ss-01",
					title: "Fertilisation report",
				},
				{
					id: "ivf-step-6-embryo-development-ss-02",
					title: "Embryo culture & updates",
				},
				{
					id: "ivf-step-6-embryo-development-ss-03",
					title: "PGT-A genetic testing (optional)",
				},
				{
					id: "ivf-step-6-embryo-development-ss-04",
					title: "Embryo grading & storage",
				},
			],
			highlight: {
				dayCount: "5–6 days",
				monitoringSchedule:
					"Monitor embryo development updates daily in the app; Decide on PGT-A testing before Day 5",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Not all fertilised eggs reach the blastocyst stage — this is a natural part of the process, not a sign that anything went wrong. Many people find daily updates both reassuring and difficult to wait for.",
				milestone: "Blastocyst Report, est. May 9",
				currentDayLabel: "Day [N] of ~6",
				estimatedLengthDays: 6,
			},
			tasks: [
				{
					id: "ivf-step-6-embryo-development-ss1-task1",
					title: "Notify: Fertilisation Report Received",
					description: "Let your team know the fertilisation report is in",
					category: "clinical",
					deadline: "Day 1 Post Retrieval — morning",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-6-embryo-development-ss-01",
					subStepTitle: "Fertilisation report",
				},
				{
					id: "ivf-step-6-embryo-development-ss1-task2",
					title: "Upload: Fertilisation Report",
					description: "Number of normally fertilised eggs (2PN)",
					category: "clinical",
					deadline: "Day 1 Post Retrieval — same day",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-6-embryo-development-ss-01",
					subStepTitle: "Fertilisation report",
				},
				{
					id: "ivf-step-6-embryo-development-ss2-task1",
					title: "Monitor: Embryo Development Updates",
					description: "Track daily embryo progress updates from the lab",
					category: "clinical",
					deadline: "Days 1–6 Post Retrieval",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-6-embryo-development-ss-02",
					subStepTitle: "Embryo culture & updates",
				},
				{
					id: "ivf-step-6-embryo-development-ss3-task1",
					title: "Review & Sign: PGT-A Consent",
					description: "Optional genetic testing for chromosomal screening",
					category: "clinical",
					deadline: "Before Biopsy Day",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-6-embryo-development-ss-03",
					subStepTitle: "PGT-A genetic testing (optional)",
				},
				{
					id: "ivf-step-6-embryo-development-ss3-task2",
					title: "Test: Embryo Biopsy for PGT-A",
					description: "Trophectoderm biopsy for chromosomal analysis",
					category: "clinical",
					deadline: "Day 5–7 Blastocyst Stage",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-6-embryo-development-ss-03",
					subStepTitle: "PGT-A genetic testing (optional)",
				},
				{
					id: "ivf-step-6-embryo-development-ss3-task3",
					title: "Upload: PGT-A Results",
					description: "Chromosomal status of each biopsied embryo",
					category: "clinical",
					deadline: "When Results Arrive (~7–14 Days)",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-6-embryo-development-ss-03",
					subStepTitle: "PGT-A genetic testing (optional)",
				},
				{
					id: "ivf-step-6-embryo-development-ss4-task1",
					title: "Fill: Embryo Storage Preference Form",
					description: "Confirm your cryopreservation or discard instructions",
					category: "clinical",
					deadline: "Before Day 6",
					status: "ongoing",
					progress: 75,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-6-embryo-development-ss-04",
					subStepTitle: "Embryo grading & storage",
					progressTotal: 4,
					progressUnit: "fields",
				},
			],
			faqs: [
				{
					id: "ivf-step-6-embryo-development-faq-1",
					question: "What happens to the eggs in the lab?",
					answer:
						"After retrieval, mature eggs are assessed and fertilised — either by mixing with sperm (conventional IVF) or by injecting a single sperm directly into each egg (ICSI). The fertilised eggs are placed in a specialised incubator that replicates the conditions of the body. Daily assessment tracks development from fertilisation through to the blastocyst stage, where embryos are graded and either transferred or frozen.",
				},
				{
					id: "ivf-step-6-embryo-development-faq-2",
					question: "Why do some embryos stop developing?",
					answer:
						"Attrition at each developmental stage is a natural and expected part of the IVF process, not a reflection of anything done or not done. On average, roughly 70% of mature eggs fertilise normally, around 50% of those reach day 3, and 30–50% reach blastocyst. The embryologist will provide updates throughout, and the RE will help interpret the final numbers in the context of the full treatment plan.",
				},
				{
					id: "ivf-step-6-embryo-development-faq-3",
					question: "Is PGT-A genetic testing worth considering?",
					answer:
						"PGT-A screens embryos for chromosomal abnormalities before transfer, with the goal of prioritising those most likely to implant successfully. It isn't recommended for everyone — the RE will advise based on age, history, and embryo numbers. It adds cost and requires a biopsy on Day 5–6. There is a small chance that a viable embryo may return an inconclusive result. It's a decision worth discussing with the medical team well before retrieval day.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Embryo Development.",
		},
		{
			id: "ivf-step-7-embryo-transfer",
			order: 7,
			title: "Embryo Transfer",
			description:
				"This step places one or more embryos into the uterus through a thin catheter — a quick, usually painless procedure that marks the beginning of the two-week wait.",
			estimatedDateRange:
				"Est. May 12 – May 14, 2026 [Day 3–5 post retrieval for fresh · or FET date]",
			estimatedStartDate: "2026-05-12",
			status: "future",
			subSteps: [
				{
					id: "ivf-step-7-embryo-transfer-ss-01",
					title: "Transfer preparation",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss-02",
					title: "Transfer procedure",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss-03",
					title: "Post-transfer medication",
				},
			],
			highlight: {
				dayCount: "1–3 days",
				monitoringSchedule:
					"Complete lining check 1 week before transfer; Complete pre-transfer checklist the day before; Begin progesterone support from transfer day",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Transfer day carries a particular weight — hope, anxiety, and anticipation together. Many people find it helpful to bring someone along, even just to wait.",
				milestone: "Embryo Transfer Complete, est. May 14",
				currentDayLabel: "Day [N] of ~3",
				estimatedLengthDays: 3,
			},
			tasks: [
				{
					id: "ivf-step-7-embryo-transfer-ss1-task1",
					title: "Book: Endometrial Lining Check",
					description: "Uterine lining thickness and pattern assessment",
					category: "clinical",
					deadline: "~1 Week Before Transfer",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-7-embryo-transfer-ss-01",
					subStepTitle: "Transfer preparation",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss1-task2",
					title: "Test: Endometrial Lining Check",
					description: "Lining must reach ≥7mm before transfer proceeds",
					category: "clinical",
					deadline: "~1 Week Before Transfer",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-7-embryo-transfer-ss-01",
					subStepTitle: "Transfer preparation",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss1-task3",
					title: "Review & Sign: Embryo Transfer Consent",
					description: "Embryo selection, transfer risks and storage plan",
					category: "clinical",
					deadline: "Before Transfer Day",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-7-embryo-transfer-ss-01",
					subStepTitle: "Transfer preparation",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss1-task4",
					title: "Book: Embryo Transfer",
					description: "Confirm your transfer date and time with your team",
					category: "clinical",
					deadline: "3–5 Days Post Retrieval or FET Date",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-7-embryo-transfer-ss-01",
					subStepTitle: "Transfer preparation",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss2-task1",
					title: "Fill: Pre-Transfer Checklist",
					description: "Bladder prep, medication and arrival confirmed",
					category: "clinical",
					deadline: "Day Before Transfer",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-7-embryo-transfer-ss-02",
					subStepTitle: "Transfer procedure",
					progressTotal: 5,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss2-task2",
					title: "Test: Embryo Transfer Procedure",
					description: "Ultrasound-guided embryo placement into uterus",
					category: "clinical",
					deadline: "Transfer Day — at your booked appointment time",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-7-embryo-transfer-ss-02",
					subStepTitle: "Transfer procedure",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss3-task1",
					title: "Review & Sign: Progesterone Support Protocol",
					description: "Luteal phase progesterone dosing instructions",
					category: "medication",
					deadline: "Transfer Day",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "ivf-step-7-embryo-transfer-ss-03",
					subStepTitle: "Post-transfer medication",
				},
				{
					id: "ivf-step-7-embryo-transfer-ss3-task2",
					title: "Monitor: Progesterone Support Medications",
					description: "Log doses taken and track any side effects",
					category: "medication",
					deadline: "Daily From Transfer Day",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-7-embryo-transfer-ss-03",
					subStepTitle: "Post-transfer medication",
				},
			],
			faqs: [
				{
					id: "ivf-step-7-embryo-transfer-faq-1",
					question: "What does the transfer procedure involve?",
					answer:
						"Embryo transfer is an outpatient procedure taking around 15–20 minutes, typically without sedation. With a slightly full bladder for ultrasound guidance, the doctor passes a soft catheter through the cervix and deposits the embryo into the uterine cavity. The process is visible on the ultrasound screen. Afterwards, a brief rest is offered before going home. There is no evidence that bed rest following transfer improves outcomes.",
				},
				{
					id: "ivf-step-7-embryo-transfer-faq-2",
					question: "How many embryos should be transferred?",
					answer:
						"Single embryo transfer (SET) is the standard recommendation in most cases — particularly for patients under 37 with good-quality embryos. Transferring one embryo significantly reduces the risk of multiple pregnancy and associated complications, without reducing overall success rates across cycles. The RE will make a specific recommendation based on age, embryo quality, and transfer history.",
				},
				{
					id: "ivf-step-7-embryo-transfer-faq-3",
					question: "What to do — and avoid — after transfer?",
					answer:
						"Normal, gentle daily activity is appropriate after transfer. There is no need for bed rest. Strenuous exercise, heavy lifting, and alcohol are best avoided. All prescribed medications — especially progesterone support — should be continued exactly as directed without stopping early. Many people find light activity helpful during the emotional intensity of the two-week wait that follows.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Embryo Transfer.",
		},
		{
			id: "ivf-step-8-two-week-wait",
			order: 8,
			title: "Two-Week Wait",
			description:
				"This step is the waiting period between embryo transfer and the pregnancy blood test. Medications continue, symptoms are logged, and support is available throughout.",
			estimatedDateRange: "Est. May 14 – May 28, 2026 [Day 1–14 post transfer]",
			estimatedStartDate: "2026-05-14",
			status: "future",
			subSteps: [
				{
					id: "ivf-step-8-two-week-wait-ss-01",
					title: "Progesterone continuation",
				},
				{
					id: "ivf-step-8-two-week-wait-ss-02",
					title: "Symptom & wellbeing monitoring",
				},
				{
					id: "ivf-step-8-two-week-wait-ss-03",
					title: "Emotional support",
				},
				{
					id: "ivf-step-8-two-week-wait-ss-04",
					title: "Beta hCG test booking",
				},
			],
			highlight: {
				dayCount: "10–14 days",
				monitoringSchedule:
					"Daily progesterone support — do not stop without clinic instruction; Log symptoms daily in the app; Book Beta hCG test at Day 10–14 post transfer",
				staffInfo: "Nurse B. Chen",
				headsUpNote:
					"The two-week wait is widely considered one of the hardest parts of IVF. Symptoms — or the absence of them — cannot reliably predict the outcome. This is not something to try to read into.",
				milestone: "Beta hCG Test, est. May 28",
				currentDayLabel: "Day [N] of ~14",
				estimatedLengthDays: 14,
			},
			tasks: [
				{
					id: "ivf-step-8-two-week-wait-ss1-task1",
					title: "Monitor: Medication Side Effects",
					description: "Log your daily response to progesterone support",
					category: "medication",
					deadline: "Daily During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-8-two-week-wait-ss-01",
					subStepTitle: "Progesterone continuation",
				},
				{
					id: "ivf-step-8-two-week-wait-ss2-task1",
					title: "Monitor: TWW Symptoms",
					description: "Track cramping, spotting and early pregnancy signs",
					category: "clinical",
					deadline: "Daily During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-8-two-week-wait-ss-02",
					subStepTitle: "Symptom & wellbeing monitoring",
				},
				{
					id: "ivf-step-8-two-week-wait-ss2-task2",
					title: "Monitor: Wellbeing & Mood",
					description: "Daily emotional check-in during the two-week wait",
					category: "cycle",
					deadline: "Daily During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "ivf-step-8-two-week-wait-ss-02",
					subStepTitle: "Symptom & wellbeing monitoring",
				},
				{
					id: "ivf-step-8-two-week-wait-ss2-task3",
					title: "Notify: New Symptom or Side Effect",
					description: "Report heavy bleeding or severe pain immediately",
					category: "clinical",
					deadline: "As Soon as It Occurs",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-8-two-week-wait-ss-02",
					subStepTitle: "Symptom & wellbeing monitoring",
				},
				{
					id: "ivf-step-8-two-week-wait-ss3-task1",
					title: "Book: Emotional Support Session",
					description: "Talk to a counsellor on your care team",
					category: "cycle",
					deadline: "Anytime During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-8-two-week-wait-ss-03",
					subStepTitle: "Emotional support",
				},
				{
					id: "ivf-step-8-two-week-wait-ss4-task1",
					title: "Book: Pregnancy Blood Test — Beta hCG",
					description: "Confirm pregnancy after your embryo transfer",
					category: "clinical",
					deadline: "10–14 Days Post Transfer",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-8-two-week-wait-ss-04",
					subStepTitle: "Beta hCG test booking",
				},
			],
			faqs: [
				{
					id: "ivf-step-8-two-week-wait-faq-1",
					question:
						"Is taking a home pregnancy test during the wait recommended?",
					answer:
						"Home tests are generally not recommended during this period. False positives can occur from residual trigger shot hCG still in the system, and false negatives can appear because levels may not yet be high enough to detect. The clinic's blood test measures the exact hCG level rather than a simple positive or negative, making it far more informative. Waiting for the official result, though harder, gives the most reliable picture.",
				},
				{
					id: "ivf-step-8-two-week-wait-faq-2",
					question: "What physical symptoms are common during this step?",
					answer:
						"Symptoms during the two-week wait are largely driven by progesterone support medication rather than whether implantation has occurred. Bloating, breast tenderness, spotting, and fatigue are all common and may appear regardless of outcome. The absence of symptoms doesn't indicate the cycle hasn't worked, and their presence doesn't confirm it has. Heavy bleeding, severe pain, or fever should be reported to the clinic immediately.",
				},
				{
					id: "ivf-step-8-two-week-wait-faq-3",
					question: "How do people typically cope with the emotional wait?",
					answer:
						"There is no single approach that works for everyone. Staying gently active, leaning on a trusted person, limiting time on fertility forums where outcomes aren't representative, and using the counsellor available through the care team are all strategies that many people find helpful. The emotional support session built into this step doesn't require a crisis to access — it's there for anyone finding the wait difficult.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Two-Week Wait.",
		},
		{
			id: "ivf-step-9-pregnancy-test",
			order: 9,
			title: "Pregnancy Test",
			description:
				"This is the day of the pregnancy blood test. Whatever the result, the care team is here — either to support the next steps forward, or to review the cycle and plan what comes next.",
			estimatedDateRange:
				"Est. May 28, 2026 [Day 14 post transfer · CD28–CD30]",
			estimatedStartDate: "2026-05-28",
			status: "future",
			subSteps: [
				{
					id: "ivf-step-9-pregnancy-test-ss-01",
					title: "Beta hCG blood test",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss-02",
					title: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss-03",
					title: "Negative outcome — cycle review",
				},
			],
			highlight: {
				dayCount: "1–3 days",
				monitoringSchedule:
					"Attend Beta hCG blood test on scheduled date — do not test early; Upload result and notify clinic same day; Book confirmation scan if positive",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Today brings a significant result — and significant feelings either way. Having someone available — to celebrate or to lean on — is something many people find important.",
				milestone: "Beta hCG Result, est. May 28",
				currentDayLabel: "Day [N] of ~3",
				estimatedLengthDays: 3,
			},
			tasks: [
				{
					id: "ivf-step-9-pregnancy-test-ss1-task1",
					title: "Test: Pregnancy Blood Test — Beta hCG",
					description: "Confirm pregnancy after your embryo transfer",
					category: "clinical",
					deadline: "14 Days Post Transfer — at your booked time",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-9-pregnancy-test-ss-01",
					subStepTitle: "Beta hCG blood test",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss1-task2",
					title: "Upload: Beta hCG Result",
					description: "Result triggers positive or negative pathway",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "upload",
					subStepId: "ivf-step-9-pregnancy-test-ss-01",
					subStepTitle: "Beta hCG blood test",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss1-task3",
					title: "Notify: Test Results Received",
					description: "Let your team know your Beta hCG results are in",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "notify",
					subStepId: "ivf-step-9-pregnancy-test-ss-01",
					subStepTitle: "Beta hCG blood test",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss2-task1",
					title: "Book: Confirmation Ultrasound Scan",
					description: "Heartbeat confirmation scan if Beta hCG positive",
					category: "clinical",
					deadline: "~6–7 Weeks Gestation",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-9-pregnancy-test-ss-02",
					subStepTitle: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss2-task2",
					title: "Test: Confirmation Ultrasound Scan",
					description: "Confirm gestational sac, fetal pole and heartbeat",
					category: "clinical",
					deadline: "~6–7 Weeks Gestation",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "test",
					subStepId: "ivf-step-9-pregnancy-test-ss-02",
					subStepTitle: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss2-task3",
					title: "Book: OB Handoff Appointment",
					description: "Transfer of care from fertility clinic to OB/GYN",
					category: "clinical",
					deadline: "~8–10 Weeks Gestation",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-9-pregnancy-test-ss-02",
					subStepTitle: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss3-task1",
					title: "Fill: Cycle Review Form",
					description: "Document your cycle outcomes for clinical review",
					category: "clinical",
					deadline: "Within 1 Week of Result",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "fill",
					subStepId: "ivf-step-9-pregnancy-test-ss-03",
					subStepTitle: "Negative outcome — cycle review",
					progressTotal: 5,
					progressUnit: "fields",
				},
				{
					id: "ivf-step-9-pregnancy-test-ss3-task2",
					title: "Book: Cycle Review Consultation",
					description: "Discuss your outcomes and next steps with your RE",
					category: "clinical",
					deadline: "Within 2 Weeks of Result",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "ivf-step-9-pregnancy-test-ss-03",
					subStepTitle: "Negative outcome — cycle review",
				},
			],
			faqs: [
				{
					id: "ivf-step-9-pregnancy-test-faq-1",
					question: "What does the Beta hCG level actually mean?",
					answer:
						"Beta hCG is the hormone produced after a fertilised egg implants. A level above 25 mIU/mL is generally considered positive, though individual clinic thresholds vary. A single number is less informative than the trend — the clinic may request a repeat test 48 hours later to confirm levels are rising appropriately, which indicates a healthy early pregnancy. A very high first number doesn't automatically mean twins, and a lower-than-expected number doesn't automatically mean the pregnancy isn't viable.",
				},
				{
					id: "ivf-step-9-pregnancy-test-faq-2",
					question: "What happens if the result is negative?",
					answer:
						"A negative result is one of the most difficult moments in the IVF process. Giving it time and space before thinking about next steps is entirely valid. A cycle review consultation — usually within 1–2 weeks — will follow, where the team reviews what happened, considers protocol adjustments, and outlines the path forward. Many people go on to have successful pregnancies after one or more unsuccessful cycles. Each cycle generates information that the team uses to improve the next.",
				},
				{
					id: "ivf-step-9-pregnancy-test-faq-3",
					question: "What happens after a positive result?",
					answer:
						"After a positive Beta hCG, a repeat blood test 48 hours later confirms the level is rising, followed by a confirmation ultrasound at around 6–7 weeks to check for a gestational sac, fetal pole, and heartbeat. If all is well, care transfers from the fertility clinic to an OB/GYN at around 8–10 weeks — a milestone known as graduation. The clinic will advise on when to taper and eventually stop progesterone support once the pregnancy is established.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Pregnancy Test.",
		},
	],
};
